import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CreditCard, Globe, User } from 'lucide-react';
import { toast } from 'sonner';

const PaymentMethodsConfig = () => {
  const [config, setConfig] = useState(null);
  const [minCoins, setMinCoins] = useState('100000');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/payment-methods/config');
      setConfig(response.data);
      setMinCoins(String(response.data.mbway_min_coins ?? 100000));
    } catch (error) {
      console.error('Erro ao carregar configuração de métodos de pagamento:', error);
      toast.error('Erro ao carregar métodos de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (updates) => {
    setUpdating(true);
    try {
      const updateData = { 
        pix_enabled: config?.pix_enabled ?? true,
        mbway_enabled: config?.mbway_enabled ?? false,
        mbway_min_coins: config?.mbway_min_coins ?? 100000,
        pix_gateway_brazilian: config?.pix_gateway_brazilian ?? 'efi',
        pix_gateway_foreigner: config?.pix_gateway_foreigner ?? 'efi',
        ...updates
      };
      const response = await api.put('/payment-methods/config', updateData);
      
      if (response.data.success) {
        setConfig(response.data.config);
        setMinCoins(String(response.data.config.mbway_min_coins ?? 100000));
        toast.success('Configuração atualizada!');
      }
    } catch (error) {
      console.error('Erro ao atualizar configuração:', error);
      toast.error('Erro ao atualizar configuração');
      loadConfig();
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center p-8">
          <FoxSpinnerInline size="md" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-green-600" />
          Métodos de Pagamento
        </CardTitle>
        <CardDescription>
          Configure os gateways de pagamento para brasileiros e estrangeiros
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* PIX */}
        <div className="p-4 border rounded-lg bg-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="text-2xl">💳</div>
              <div>
                <Label htmlFor="pix-enabled" className="text-base font-semibold cursor-pointer">
                  PIX
                </Label>
                <p className="text-sm text-gray-600">
                  Pagamento instantâneo via PIX
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={config?.pix_enabled ? 'default' : 'secondary'} className="min-w-[70px] justify-center">
                {config?.pix_enabled ? 'ATIVO' : 'INATIVO'}
              </Badge>
              <Switch
                id="pix-enabled"
                checked={config?.pix_enabled}
                onCheckedChange={(checked) => handleUpdate({ pix_enabled: checked })}
                disabled={updating}
              />
            </div>
          </div>
          
          {/* Configuração de Gateways por tipo de cliente */}
          {config?.pix_enabled && (
            <div className="mt-4 pt-4 border-t space-y-4">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Configurar Gateway PIX por tipo de cliente:
              </p>
              
              {/* Gateway para Brasileiros */}
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-green-600" />
                  <div>
                    <Label className="text-sm font-medium text-green-800">🇧🇷 Brasileiros</Label>
                    <p className="text-xs text-green-600">Clientes com CPF válido</p>
                  </div>
                </div>
                <Select
                  value={config?.pix_gateway_brazilian || 'efi'}
                  onValueChange={(value) => handleUpdate({ pix_gateway_brazilian: value })}
                  disabled={updating}
                >
                  <SelectTrigger className="w-[180px] bg-white" data-testid="pix-gateway-brazilian-select">
                    <SelectValue placeholder="Selecionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efi" data-testid="gateway-option-efi">
                      <span className="text-green-600 font-medium">EFI</span>
                    </SelectItem>
                    <SelectItem value="stripe" data-testid="gateway-option-stripe">
                      <span className="text-purple-600 font-medium">Stripe</span>
                    </SelectItem>
                    <SelectItem value="mercadopago" data-testid="gateway-option-mercadopago">
                      <span className="text-sky-600 font-medium">Mercado Pago</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Gateway para Estrangeiros - Apenas EFI */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-blue-600" />
                  <div>
                    <Label className="text-sm font-medium text-blue-800">🌍 Estrangeiros</Label>
                    <p className="text-xs text-blue-600">Clientes sem CPF (cobrança local)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-green-300 text-green-600 bg-green-50">
                    EFI
                  </Badge>
                  <span className="text-xs text-gray-400">(único disponível)</span>
                </div>
              </div>
            </div>
          )}
        </div>

              {/* MB WAY */}
              <div className="p-4 border rounded-lg bg-white space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">📱</div>
                    <div>
                      <Label htmlFor="mbway-enabled" className="text-base font-semibold cursor-pointer">
                        MB WAY (Portugal)
                      </Label>
                      <p className="text-sm text-gray-600">
                        Cobrança em euro (câmbio Wise) com a taxa da Stripe repassada ao cliente
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={config?.mbway_enabled ? 'default' : 'secondary'} className="min-w-[70px] justify-center">
                      {config?.mbway_enabled ? 'ATIVO' : 'INATIVO'}
                    </Badge>
                    <Switch
                      id="mbway-enabled"
                      checked={config?.mbway_enabled}
                      onCheckedChange={(checked) => handleUpdate({ mbway_enabled: checked })}
                      disabled={updating}
                    />
                  </div>
                </div>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <Label className="text-sm font-medium text-indigo-900">
                    Pacote mínimo para MB WAY (moedas)
                  </Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <Input type="number" min="0" step="1000" className="max-w-[180px] bg-white"
                      data-testid="mbway-min-coins-input"
                      value={minCoins}
                      onChange={(e) => setMinCoins(e.target.value)} />
                    <Button size="sm" disabled={updating} data-testid="mbway-min-coins-save"
                      onClick={() => handleUpdate({ mbway_min_coins: parseInt(minCoins, 10) || 0 })}>
                      Salvar
                    </Button>
                  </div>
                  <p className="mt-1.5 text-xs text-indigo-700">
                    A Stripe cobra € 0,25 fixos por pagamento MB WAY — em pacote pequeno isso vira
                    20-35% do valor. Abaixo deste mínimo o MB WAY não aparece no checkout.
                    A taxa repassada é configurada em <b>Finanças → Taxas Gateway</b>.
                  </p>
                </div>
              </div>

        {/* Info */}
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-600">
            Os métodos desativados não aparecerão como opção para os clientes no checkout.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentMethodsConfig;
